import {
    api,
    apiErrorMessage,
    ListChoiceFilter,
    useAuthStore,
    useSetObjectColor,
    useZodForm,
    type ColorKey,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    Checkbox,
    ColorSwatchPicker,
    ColorTag,
    ConfirmDialog,
    EditIcon,
    Field,
    FormSheet,
    Input,
    PageHeader,
    RemoveIcon,
    RevealIcon,
    RowActions,
    type ListColumnDef,
    type ListViewPreset,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { z } from 'zod';

import type { Role } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { P } from '@/lib/permissions';
import { useWrite } from '@/lib/queries';

const schema = z.object({
    key: z.string().regex(/^[a-z][a-z0-9_]*$/, 'lower_snake_case'),
    name: z.string().min(1, 'A name'),
    permissions: z.array(z.string()).min(1, 'At least one permission'),
});

/** The permission catalogue as the roles screen offers it: every key a staff role may carry, grouped by service. */
export function catalogue(roles: Role[]): string[] {
    const keys = new Set<string>(Object.values(P));
    for (const r of roles) for (const p of r.permissions) keys.add(p);
    return [...keys].sort();
}

const PATH = '/staff/roles';
const PAGE_KEY = 'staff.roles';

/** This page is about staff roles (the merchant roles are the dashboard's); "All roles" shows both. */
export const ROLE_PRESETS: ListViewPreset[] = [
    { key: 'staff', name: 'Staff roles', filters: { audience: ['staff'] } },
    { key: 'all', name: 'All roles', filters: {} },
];

/** The route's prefetch: the person's views, then the roles of the view (BM-46 §2.0 + §2.4). */
export const rolesPrefetch = adminListPrefetch(PAGE_KEY, PATH, {
    presets: ROLE_PRESETS,
});

const roleFilters = filterDefs(PATH, {
    q: 'Search',
    system: 'Kind',
    audience: 'Audience',
});

export function RolesPage() {
    const can = useAuthStore((s) => s.can);
    const [editing, setEditing] = useState<Role | 'new' | null>(null);
    const [deleting, setDeleting] = useState<Role | null>(null);
    const [error, setError] = useState<string | null>(null);
    // BM-46 §2.5: the role's label color (an object color, saved apart from the role)
    const [color, setColor] = useState<ColorKey | null>(null);
    const setObjectColor = useSetObjectColor();
    const form = useZodForm(schema, {
        defaultValues: { key: '', name: '', permissions: [] },
    });
    const save = useWrite(
        async (body: { role: Role | 'new'; values: z.infer<typeof schema> }) =>
            body.role === 'new'
                ? (await api().post<Role>('/staff/roles', body.values)).data
                : (
                      await api().patch<Role>(`/staff/roles/${body.role.id}`, {
                          ...body.values,
                          version: body.role.version,
                      })
                  ).data,
        ['/staff/roles'],
    );
    const remove = useWrite(
        async (r: Role) => api().delete(`/staff/roles/${r.id}`),
        ['/staff/roles'],
    );
    const onError = (e: unknown) => setError(apiErrorMessage(e));
    const open = (r: Role | 'new') => {
        setEditing(r);
        setColor(r === 'new' ? null : r.color);
        form.reset(
            r === 'new'
                ? { key: '', name: '', permissions: [] }
                : { key: r.key, name: r.name, permissions: [...r.permissions] },
        );
    };
    const columns: ListColumnDef<Role>[] = [
        {
            key: 'name',
            label: 'Role',
            sortKey: 'name',
            cell: (r) => (
                <span>
                    <ColorTag color={r.color ?? null}>{r.name}</ColorTag>{' '}
                    <span className="text-muted-foreground ml-1 font-mono text-xs">
                        {r.key}
                    </span>
                </span>
            ),
            locked: 'start',
        },
        {
            key: 'system',
            aliases: ['kind'],
            label: 'Kind',
            sortKey: 'system',
            cell: (r) => (r.system ? 'built in' : 'custom'),
        },
        {
            key: 'count',
            label: 'Permissions',
            cell: (r) => r.permissions.length,
            noTruncate: true,
        },
        {
            key: 'actions',
            label: 'Actions',
            locked: 'end',
            actions: true,
            // BM-84 (rule G.6, design 0.7.0 roles): Edit (View for built-in roles) and Delete for custom roles
            cell: (r) => (
                <RowActions
                    name={r.name}
                    items={[
                        {
                            label:
                                r.system || !can(P.rolesManage)
                                    ? 'View'
                                    : 'Edit',
                            icon:
                                r.system || !can(P.rolesManage)
                                    ? RevealIcon
                                    : EditIcon,
                            onSelect: () => open(r),
                        },
                        {
                            label: 'Delete',
                            icon: RemoveIcon,
                            destructive: true,
                            hidden: r.system || !can(P.rolesManage),
                            onSelect: () => setDeleting(r),
                        },
                    ]}
                />
            ),
        },
    ];
    const { state, query } = useAdminList<Role>(PAGE_KEY, PATH, {
        columns,
        filters: roleFilters,
        presets: ROLE_PRESETS,
    });
    const staffRoles = query.rows.filter((r) => r.audience === 'staff');
    const readOnly =
        editing !== 'new' && (editing?.system || !can(P.rolesManage));
    return (
        <div>
            <PageHeader
                crumbs={[{ label: 'Staff', href: '/staff' }]}
                title="Staff roles"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                'Roles bundle permissions from the catalog; the services only ever see permissions. Built-in roles are read-only.'
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
                primaryAction={
                    can(P.rolesManage) ? (
                        <Button onClick={() => open('new')}>New role</Button>
                    ) : null
                }
            />
            <AdminListPage
                noun="roles"
                state={state}
                query={query}
                rowKey={(r) => r.id}
                board={false}
                exportable={false}
                controls={{
                    q: { kind: 'search' },
                    system: (
                        <ListChoiceFilter
                            state={state}
                            name="system"
                            label="Kind"
                            options={[
                                { value: 'true', label: 'Built in' },
                                { value: 'false', label: 'Custom' },
                            ]}
                        />
                    ),
                }}
                empty={
                    <p className="text-muted-foreground p-6 text-center text-sm">
                        No roles.
                    </p>
                }
            />
            {/* BM-84 (rule E.9, design 0.7.0 roles): create and edit in a sheet; built-in roles open read-only */}
            <FormSheet
                open={editing !== null}
                onOpenChange={(o) => {
                    if (!o) {
                        setEditing(null);
                        setError(null);
                    }
                }}
                size="md"
                title={
                    editing === 'new'
                        ? 'New staff role'
                        : readOnly
                          ? `${editing?.name ?? 'Role'} (read-only)`
                          : `Edit ${editing?.name ?? 'role'}`
                }
                submitLabel={editing === 'new' ? 'Create role' : 'Save role'}
                busy={save.isPending}
                submitDisabled={readOnly}
                dirty={
                    !readOnly &&
                    (form.formState.isDirty ||
                        color !==
                            (editing && editing !== 'new'
                                ? editing.color
                                : null))
                }
                error={error}
                formatError={(e) => String(e)}
                data-testid="role-form"
                onSubmit={form.handleSubmit((values) => {
                    if (!editing || readOnly) return;
                    setError(null);
                    const before = editing === 'new' ? null : editing.color;
                    save.mutate(
                        { role: editing, values },
                        {
                            onSuccess: (role) => {
                                if (color !== before)
                                    setObjectColor.mutate(
                                        { type: 'role', id: role.id, color },
                                        { onError },
                                    );
                                setEditing(null);
                            },
                            onError,
                        },
                    );
                })}
            >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field
                        id="key"
                        label="Key"
                        error={form.formState.errors.key?.message}
                    >
                        <Input
                            id="key"
                            readOnly={readOnly || editing !== 'new'}
                            {...form.register('key')}
                        />
                    </Field>
                    <Field
                        id="name"
                        label="Name"
                        error={form.formState.errors.name?.message}
                    >
                        <Input
                            id="name"
                            readOnly={readOnly}
                            {...form.register('name')}
                        />
                    </Field>
                </div>
                <Field id="role-color" label="Color">
                    <ColorSwatchPicker
                        value={color}
                        onChange={setColor}
                        disabled={readOnly}
                        label="Role color"
                    />
                </Field>
                <Field
                    id="permissions"
                    label="Permissions"
                    error={form.formState.errors.permissions?.message}
                >
                    <ul className="grid max-h-72 grid-cols-1 gap-1 overflow-y-auto md:grid-cols-2">
                        {catalogue(staffRoles).map((p) => (
                            <li key={p}>
                                <label className="flex items-center gap-2 font-mono text-xs">
                                    <Checkbox
                                        disabled={readOnly}
                                        checked={form
                                            .watch('permissions')
                                            .includes(p)}
                                        onCheckedChange={(v) => {
                                            const current =
                                                form.getValues('permissions');
                                            form.setValue(
                                                'permissions',
                                                v === true
                                                    ? [...current, p]
                                                    : current.filter(
                                                          (k) => k !== p,
                                                      ),
                                                {
                                                    shouldValidate: true,
                                                },
                                            );
                                        }}
                                    />
                                    {p}
                                </label>
                            </li>
                        ))}
                    </ul>
                </Field>
            </FormSheet>
            <ConfirmDialog
                open={deleting !== null}
                onOpenChange={(o) => (o ? null : setDeleting(null))}
                title={`Delete ${deleting?.name ?? 'this role'}`}
                description="Identity refuses while a staff member still holds it."
                confirmLabel="Delete"
                destructive
                busy={remove.isPending}
                onConfirm={() =>
                    deleting &&
                    remove.mutate(deleting, {
                        onSuccess: () => setDeleting(null),
                        onError,
                    })
                }
            />
        </div>
    );
}
